import * as React from "react";
import type { KnowledgeBaseFile, KnowledgeBaseFilesPagination } from "../../types/knowledgeBase";
import {
  deleteKnowledgeBaseFile,
  listKnowledgeBaseFilesPage,
  uploadKnowledgeBaseFile,
} from "../../api/knowledgeBase";
import { useDebouncedValue } from "../../hooks/useDebouncedSearchFilter";
import { ChatOutlineBtn } from "./ui/ChatOutlineBtn";
import { ChatMicroBtn } from "./ui/ChatMicroBtn";
import { ChatSpinner } from "./ui/ChatSpinner";
import { IconClose, IconPlus, IconReload, IconSearch } from "./icons/ChatIcons";

export type ChatKnowledgeBasePanelProps = {
  active?: boolean;
  onKbSelectedFileIdsChange?: (fileIds: string[]) => void;
  onReferenceFileLabelsChange?: (items: Array<{ id: string; fileName: string }>) => void;
};

const PER_PAGE = 5;
const SEARCH_DEBOUNCE_MS = 300;

function maxAllowedPageForClamp(
  requestedPage: number,
  res: { files: readonly unknown[]; pagination: KnowledgeBaseFilesPagination },
): number {
  const apiMax = Math.max(1, res.pagination.totalPages || 1);
  if (res.files.length > 0 || res.pagination.hasNext) {
    return Math.max(apiMax, requestedPage);
  }
  return apiMax;
}

function minTotalItemsForCurrentFetch(page: number, fileCount: number): number {
  return Math.max(0, (page - 1) * PER_PAGE + fileCount);
}

export const ChatKnowledgeBasePanel: React.FC<ChatKnowledgeBasePanelProps> = ({
  active = true,
  onKbSelectedFileIdsChange,
  onReferenceFileLabelsChange,
}) => {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const [files, setFiles] = React.useState<KnowledgeBaseFile[]>([]);
  const [pagination, setPagination] = React.useState<KnowledgeBaseFilesPagination | null>(null);
  const [page, setPage] = React.useState(1);
  const [searchInput, setSearchInput] = React.useState("");
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);
  const [refreshNonce, setRefreshNonce] = React.useState(0);
  const [listLoading, setListLoading] = React.useState(false);
  const [listError, setListError] = React.useState<string | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = React.useState<string | null>(null);
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(() => new Set());
  const fileIdToNameRef = React.useRef<Map<string, string>>(new Map());

  const lastCompletedListFetchRef = React.useRef<{
    page: number;
    search: string;
    refreshNonce: number;
  } | null>(null);
  const prevDebouncedSearchForPageResetRef = React.useRef<string | null>(null);
  const listTotalsEpochKeyRef = React.useRef<string>("");

  const notifySelection = React.useCallback(
    (next: Set<string>) => {
      onKbSelectedFileIdsChange?.(Array.from(next));
      const labels: Array<{ id: string; fileName: string }> = [];
      next.forEach((id) => {
        const fileName =
          fileIdToNameRef.current.get(id) ?? files.find((f) => f.id === id)?.fileName ?? "";
        labels.push({ id, fileName: fileName || id });
      });
      onReferenceFileLabelsChange?.(labels);
    },
    [onKbSelectedFileIdsChange, onReferenceFileLabelsChange, files],
  );

  React.useEffect(() => {
    notifySelection(selectedIds);
  }, [selectedIds, notifySelection]);

  React.useLayoutEffect(() => {
    const prev = prevDebouncedSearchForPageResetRef.current;
    if (prev === null) {
      prevDebouncedSearchForPageResetRef.current = debouncedSearch;
      return;
    }
    if (prev === debouncedSearch) return;
    prevDebouncedSearchForPageResetRef.current = debouncedSearch;
    setPage(1);
  }, [debouncedSearch]);

  React.useEffect(() => {
    if (!active) {
      lastCompletedListFetchRef.current = null;
      listTotalsEpochKeyRef.current = "";
      return undefined;
    }

    const searchNorm = debouncedSearch.trim();
    const prevDone = lastCompletedListFetchRef.current;
    if (
      prevDone &&
      prevDone.page === page &&
      prevDone.search === searchNorm &&
      prevDone.refreshNonce === refreshNonce
    ) {
      return undefined;
    }

    let cancelled = false;

    void (async () => {
      setListError(null);
      setListLoading(true);
      try {
        let res = await listKnowledgeBaseFilesPage({
          page,
          perPage: PER_PAGE,
          search: searchNorm || undefined,
        });
        if (cancelled) return;

        let effectivePage = page;
        const apiMax = maxAllowedPageForClamp(page, res);
        const pagesKnownFromPriorTotal =
          pagination !== null && pagination.totalItems > 0
            ? Math.ceil(pagination.totalItems / PER_PAGE)
            : 0;
        const maxPage = Math.max(apiMax, pagesKnownFromPriorTotal);
        if (page > maxPage) {
          res = await listKnowledgeBaseFilesPage({
            page: maxPage,
            perPage: PER_PAGE,
            search: searchNorm || undefined,
          });
          if (cancelled) return;
          effectivePage = maxPage;
          setPage(maxPage);
        }

        setFiles(res.files);

        const totalsEpochKey = `${refreshNonce}:${searchNorm}`;
        const isNewTotalsEpoch = listTotalsEpochKeyRef.current !== totalsEpochKey;
        if (isNewTotalsEpoch) listTotalsEpochKeyRef.current = totalsEpochKey;
        const seenFloor = minTotalItemsForCurrentFetch(page, res.files.length);
        setPagination((prev) => {
          const inc = res.pagination;
          if (!prev || isNewTotalsEpoch) {
            const totalItems = Math.max(inc.totalItems, seenFloor);
            const totalPages = Math.max(1, inc.totalPages, Math.ceil(totalItems / PER_PAGE));
            return { ...inc, totalItems, totalPages };
          }
          let totalItems = Math.max(inc.totalItems, prev.totalItems, seenFloor);
          if (page > 1 && inc.totalItems < prev.totalItems) {
            totalItems = Math.max(prev.totalItems, seenFloor);
          }
          const totalPages = Math.max(
            1,
            inc.totalPages,
            prev.totalPages,
            Math.ceil(totalItems / PER_PAGE),
          );
          return { ...inc, totalItems, totalPages };
        });

        lastCompletedListFetchRef.current = {
          page: effectivePage,
          search: searchNorm,
          refreshNonce,
        };
      } catch (e) {
        if (!cancelled) {
          lastCompletedListFetchRef.current = null;
          setListError(e instanceof Error ? e.message : String(e));
          setFiles([]);
          setPagination(null);
        }
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pagination used only for clamp floor
  }, [active, page, debouncedSearch, refreshNonce]);

  const bumpRefresh = React.useCallback(() => {
    setRefreshNonce((n) => n + 1);
  }, []);

  const displayedFiles = React.useMemo(() => {
    if (files.length === 0) return files;
    if (files.length <= PER_PAGE) return files;
    const start = (page - 1) * PER_PAGE;
    return files.slice(start, start + PER_PAGE);
  }, [files, page]);

  const toggleFile = (fileId: string, checked: boolean) => {
    const meta = files.find((f) => f.id === fileId);
    if (meta?.fileName) {
      if (checked) fileIdToNameRef.current.set(fileId, meta.fileName);
      else fileIdToNameRef.current.delete(fileId);
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(fileId);
      else next.delete(fileId);
      return next;
    });
  };

  const clearReferenceSelection = () => {
    fileIdToNameRef.current.clear();
    setSelectedIds(new Set());
  };

  const onFileInputChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const input = e.target;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;
    setUploading(true);
    setListError(null);
    try {
      const created = await uploadKnowledgeBaseFile(file);
      if (created.fileName) fileIdToNameRef.current.set(created.id, created.fileName);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.add(created.id);
        return next;
      });
      setPage(1);
      bumpRefresh();
    } catch (err) {
      setListError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  };

  const onDeleteFile = async (file: KnowledgeBaseFile) => {
    setDeletingId(file.id);
    setListError(null);
    try {
      await deleteKnowledgeBaseFile(file.id);
      setSelectedIds((prev) => {
        if (!prev.has(file.id)) return prev;
        fileIdToNameRef.current.delete(file.id);
        const next = new Set(prev);
        next.delete(file.id);
        return next;
      });
      setConfirmDeleteId(null);
      bumpRefresh();
    } catch (err) {
      setListError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeletingId(null);
    }
  };

  const totalItems = pagination?.totalItems ?? 0;
  const pagerTotal = Math.max(totalItems, minTotalItemsForCurrentFetch(page, files.length));
  const emptyKb = !listLoading && totalItems === 0 && !debouncedSearch.trim();
  const noMatches = !listLoading && totalItems === 0 && debouncedSearch.trim().length > 0;
  const rangeStart = pagerTotal === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const rangeEnd = Math.min(page * PER_PAGE, pagerTotal);
  const maxPage = Math.max(1, Math.ceil(pagerTotal / PER_PAGE));

  return (
    <div style={{ width: "100%", minWidth: 0 }}>
      <div className="chat-kb-panel__toolbar">
        <div className="chat-kb-panel__actions">
          <ChatOutlineBtn onClick={bumpRefresh} disabled={listLoading} aria-label="Refresh">
            <IconReload size={12} />
            Refresh
          </ChatOutlineBtn>
          <ChatOutlineBtn
            onClick={clearReferenceSelection}
            disabled={selectedIds.size === 0}
          >
            Clear selection
          </ChatOutlineBtn>
        </div>
      </div>

      <div className="chat-kb-panel__hint-row">
        <span className="chat-kb-panel__hint">
          Upon selection, the following files will be used as context when you send.
        </span>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          accept=".pdf,.doc,.docx,.txt,.md,.rtf"
          onChange={onFileInputChange}
        />
        <ChatOutlineBtn
          primary
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          <IconPlus size={12} />
          {uploading ? "Uploading…" : "Upload"}
        </ChatOutlineBtn>
      </div>

      <div className="chat-kb-panel__search">
        <IconSearch size={12} />
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search files…"
          aria-label="Search knowledge base files"
        />
      </div>

      {listError ? (
        <span className="chat-kb-panel__error" role="alert">
          {listError}
        </span>
      ) : null}

      <div className="chat-kb-panel__list-wrap thin-scroll">
        {listLoading ? (
          <div className="chat-kb-panel__list-loading">
            <ChatSpinner size="sm" />
          </div>
        ) : null}
        {emptyKb ? (
          <span className="chat-kb-panel__empty">No documents yet. Add a file to your knowledge base.</span>
        ) : noMatches ? (
          <span className="chat-kb-panel__empty">No files match your search.</span>
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {displayedFiles.map((file) => {
              const busy = deletingId === file.id;
              const confirming = confirmDeleteId === file.id;
              return (
                <li key={file.id} className={`chat-kb-row${busy ? " is-busy" : ""}`}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(file.id)}
                    disabled={busy}
                    onChange={(e) => toggleFile(file.id, e.target.checked)}
                    aria-label={`Select ${file.fileName}`}
                  />
                  <span className="chat-kb-row__name" title={file.fileName}>
                    {file.fileName}
                  </span>
                  {confirming ? (
                    <span className="chat-kb-row__confirm">
                      Delete?
                      <ChatOutlineBtn
                        primary
                        disabled={busy}
                        onClick={() => void onDeleteFile(file)}
                      >
                        Yes
                      </ChatOutlineBtn>
                      <ChatOutlineBtn disabled={busy} onClick={() => setConfirmDeleteId(null)}>
                        No
                      </ChatOutlineBtn>
                    </span>
                  ) : (
                    <ChatMicroBtn
                      title={`Delete ${file.fileName}`}
                      aria-label={`Delete ${file.fileName}`}
                      disabled={busy}
                      onClick={() => setConfirmDeleteId(file.id)}
                    >
                      <IconClose size={12} />
                    </ChatMicroBtn>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {pagination && pagerTotal > 0 ? (
        <div className="chat-kb-pager">
          <span>
            {rangeStart}–{rangeEnd} of {pagerTotal}
          </span>
          <div className="chat-kb-pager__btns">
            <ChatOutlineBtn
              disabled={listLoading || page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              aria-label="Previous page"
            >
              Prev
            </ChatOutlineBtn>
            <ChatOutlineBtn
              disabled={listLoading || page >= maxPage}
              onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
              aria-label="Next page"
            >
              Next
            </ChatOutlineBtn>
          </div>
        </div>
      ) : null}
    </div>
  );
};
